import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'
import { RESET_PASSWORD_MUTATION } from '../../graphql/auth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import FormError from '../../components/ui/FormError'
import type { CurrentUser } from '../../contexts/AuthContext'

interface ResetPasswordData {
  resetPassword: {
    user: CurrentUser | null
    token: string | null
    errors: string[]
  }
}

interface Fields {
  password: string
  passwordConfirmation: string
}

function validate(f: Fields): Partial<Record<keyof Fields, string>> {
  const errs: Partial<Record<keyof Fields, string>> = {}
  if (!f.password) errs.password = 'Ο κωδικός είναι υποχρεωτικός'
  else if (f.password.length < 8) errs.password = 'Τουλάχιστον 8 χαρακτήρες'
  if (!f.passwordConfirmation) errs.passwordConfirmation = 'Επιβεβαιώστε τον κωδικό'
  else if (f.password !== f.passwordConfirmation) errs.passwordConfirmation = 'Οι κωδικοί δεν ταιριάζουν'
  return errs
}

export default function ResetPassword() {
  const { signIn } = useAuth()
  const { t } = useLang()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const resetToken = searchParams.get('reset_password_token') ?? ''

  const [fields, setFields] = useState<Fields>({ password: '', passwordConfirmation: '' })
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Fields, string>>>({})
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({})

  const [mutate, { loading }] = useMutation<ResetPasswordData>(RESET_PASSWORD_MUTATION)

  if (!resetToken) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <p className="text-sm text-slate-500 mb-4">
            {t('Μη έγκυρος σύνδεσμος επαναφοράς κωδικού.', 'Invalid password reset link.')}
          </p>
          <Link to="/forgot-password" className="text-primary-600 font-medium hover:underline text-sm">
            {t('Αίτηση νέου συνδέσμου', 'Request a new link')}
          </Link>
        </div>
      </div>
    )
  }

  function handleChange(key: keyof Fields, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function handleBlur(key: keyof Fields) {
    setTouched((prev) => ({ ...prev, [key]: true }))
    const errs = validate({ ...fields })
    setFieldErrors((prev) => ({ ...prev, [key]: errs[key] ?? '' }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errs = validate(fields)
    if (Object.keys(errs).length) {
      setFieldErrors(errs)
      setTouched({ password: true, passwordConfirmation: true })
      return
    }

    setServerErrors([])
    try {
      const { data } = await mutate({
        variables: {
          resetPasswordToken: resetToken,
          password: fields.password,
          passwordConfirmation: fields.passwordConfirmation,
        },
      })

      const result = data?.resetPassword
      if (!result) return

      if (result.errors.length) {
        setServerErrors(result.errors)
        return
      }

      if (result.token && result.user) {
        const signedInUser = await signIn(result.token, result.user)
        if (signedInUser.admin) {
          navigate('/admin', { replace: true })
        } else if (signedInUser.role === 'provider') {
          navigate('/provider/dashboard', { replace: true })
        } else {
          navigate('/', { replace: true })
        }
      }
    } catch {
      setServerErrors([t('Κάτι πήγε στραβά. Δοκιμάστε ξανά.', 'Something went wrong. Please try again.')])
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {t('Νέος κωδικός πρόσβασης', 'Set New Password')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('Επιλέξτε έναν ασφαλή κωδικό πρόσβασης.', 'Choose a strong password.')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormError errors={serverErrors} />

            <Input
              type="password"
              label={t('Νέος κωδικός', 'New Password')}
              placeholder="••••••••"
              value={fields.password}
              onChange={(e) => handleChange('password', e.target.value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? fieldErrors.password : ''}
              hint={t('Τουλάχιστον 8 χαρακτήρες', 'At least 8 characters')}
              required
              autoComplete="new-password"
            />

            <Input
              type="password"
              label={t('Επιβεβαίωση κωδικού', 'Confirm Password')}
              placeholder="••••••••"
              value={fields.passwordConfirmation}
              onChange={(e) => handleChange('passwordConfirmation', e.target.value)}
              onBlur={() => handleBlur('passwordConfirmation')}
              error={touched.passwordConfirmation ? fieldErrors.passwordConfirmation : ''}
              required
              autoComplete="new-password"
            />

            <Button type="submit" loading={loading} fullWidth size="lg">
              {t('Αποθήκευση νέου κωδικού', 'Save New Password')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/signin" className="text-primary-600 font-medium hover:underline">
            ← {t('Επιστροφή στη σύνδεση', 'Back to Sign In')}
          </Link>
        </p>
      </div>
    </div>
  )
}
